import React, { useState, useEffect } from "react";
import { useAuth } from "../authContext";
import { getJWT } from "../api/auth";

export default function RequestApiKey() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(user?.name ?? "");

  useEffect(() => {
    setName(user?.name ?? "");
  }, [user?.name]);

  const handleRequestKey = async () => {
    setLoading(true);
    setError(null);
    setStatus(null);
    setCopied(false);

    if (!name || !name.trim()) {
      setError("Full name is required. Update your account profile to set your full name.");
      setLoading(false);
      return;
    }

    try {
      const token = await getJWT();
      const payload = { email: user?.email, name: name.trim() };

      const res = await fetch("https://api.infauxtech.com/rest/v1/request-api-key", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setApiKey(data.apiKey);
        setStatus(data.message || "New/Existing API key status unknown.");
        setExpiresAt(data.expiresAtReadable || null); // 👈 add this
      } else {
        throw new Error(data.error || `Failed to request API key (${res.status})`);
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-lg border border-gray-300 bg-white p-6 shadow-md">
      <h2 className="text-lg font-semibold mb-4">Request Your API Key</h2>

      <div className="text-sm text-gray-600 mb-3">
        <label className="block font-medium">Email (associated)</label>
        <input
          type="email"
          value={user?.email ?? ""}
          readOnly
          className="w-full mb-3 px-3 py-2 border rounded bg-gray-100"
        />

        <label className="block font-medium">Full name (from account)</label>
        <input
          type="text"
          value={name}
          readOnly
          placeholder="Your full name from your account profile"
          className="w-full mb-2 px-3 py-2 border rounded bg-gray-100"
          aria-readonly="true"
        />

        {!name?.trim() && (
          <p className="text-xs text-yellow-700 mt-1">
            No full name is set on your account. Please update your profile (Cognito) so the correct name is saved.
          </p>
        )}
      </div>

      <button
        onClick={handleRequestKey}
        disabled={loading || !name?.trim()}
        className="px-4 py-2 bg-purple-700 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
      >
        {loading ? "Requesting…" : "Generate API Key"}
      </button>

      {/* ✅ Status + Key + Expiry */}
      {(status || apiKey) && (
        <div className="mt-4 text-sm bg-green-100 border border-green-400 text-green-800 p-3 rounded">
          {status && <p className="mb-2">{status}</p>}

          {apiKey && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="font-medium">Your API Key:</p>
                <div className="flex items-center gap-2 break-all">
                  <code className="bg-white px-2 py-1 rounded border border-green-200">{apiKey}</code>
                  <button
                    onClick={handleCopy}
                    className="text-green-700 hover:text-green-900 focus:outline-none"
                    title="Copy API Key"
                  >
                    {copied ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 111.414-1.414L8.414 12.586l7.879-7.879a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* 👇 Expiration Info */}
              {expiresAt && (
                <p className="text-xs text-gray-700 mt-1">
                  <strong>Expires on:</strong>{" "}
                  {new Date(expiresAt).toLocaleString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 text-sm bg-red-100 border border-red-400 text-red-800 p-3 rounded">
          Error: {error}
        </div>
      )}
    </div>
  );
}
